import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { CurrentOrganizationService } from "@onsip/common/services/api/apiParams/current-organization.service";
import {
  Organization,
  OrganizationService
} from "@onsip/common/services/api/resources/organization/organization.service";
import { views } from "@onsip/web/app/phone/views";
import { combineLatest, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { ShellNavigationWithLocator } from "../../shellNavigations/shell-navigations.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { AccountService } from "@onsip/common/services/api/resources/account/account.service";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { UserAddressService } from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { OrgAdminShellNavigations, ShellNavigations } from "./org-side-nav-menu-helper";

@Component({
  selector: "onsip-org-side-nav",
  templateUrl: "./org-side-nav.component.html",
  styleUrls: ["./org-side-nav.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OrgSideNavComponent implements OnInit, OnDestroy {
  views = views;
  currentOrganization = new FormControl("", { nonNullable: true });
  organizationList: Array<Organization> = [];
  shellNavigations!: Array<ShellNavigationWithLocator>;
  E2eLocators = E2eLocators;
  userService = inject(UserService);
  isAccountAdmin$ = this.userService.selfUserIsAccountAdmin$;
  userAddressService = inject(UserAddressService);
  private unsubscriber = new Subscription();

  constructor(
    private orgService: OrganizationService,
    private currentOrgService: CurrentOrganizationService,
    private router: Router,
    private accountService: AccountService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.unsubscriber.add(
      this.isAccountAdmin$.subscribe(isAccountAdmin => {
        if (isAccountAdmin) {
          this.orgService.organizationBrowse();
        }
      })
    );
    this.unsubscriber.add(
      combineLatest({
        orgs: this.orgService.state.pipe(
          filter(state => !state.loading),
          map(state => Object.values(state.state))
        ),
        accountId: this.accountService.currentAccountId()
      })
        .pipe(map(({ orgs, accountId }) => orgs.filter(org => org.accountId === accountId)))
        .subscribe(orgs => {
          this.organizationList = orgs;
          this.cdRef.markForCheck();
        })
    );

    this.unsubscriber.add(
      this.orgService.organization
        .pipe(distinctUntilChanged((a, b) => a.organizationId === b.organizationId))
        .subscribe(currentOrg => {
          this.currentOrganization.patchValue(currentOrg.organizationId, { emitEvent: false });
        })
    );

    this.unsubscriber.add(
      this.currentOrganization.valueChanges.pipe(distinctUntilChanged()).subscribe(orgId => {
        this.currentOrgService.switchCurrentOrg(orgId);
        this.loadUsers();
        this.router.navigate([views.ADMIN_PBX_OVERVIEW]);
      })
    );

    this.unsubscriber.add(
      this.userService.selfUserIsAccountAdmin$.subscribe(isAccountAdmin => {
        this.shellNavigations = isAccountAdmin ? ShellNavigations : OrgAdminShellNavigations;
      })
    );
  }

  private loadUsers() {
    this.userService.userBrowse();
    this.userAddressService.userAddressBrowseWithOrgId();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }
}
